import React, {useMemo} from 'react';
import {
    BrowseEventTrackingProvider,
    TrackableDiscoveryItem,
    DiscoveryItemTypeOptions,
} from '@udemy-v2/react-browse-event-tracking';
import {
    RecommendationItem,
    RecommendationSourceObjectType,
    RecommendationsProvider,
    useRecommendationsSections,
} from '@udemy-v2/react-recommendations';
import {StudentsAlsoBought} from './students-also-bought.react-component';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {BrowseCourse} from '@udemy-v2/browse-course';

// This is the maximum number of courses that will be shown in the Students Also Bought section.
const RECOMMENDATIONS_ITEM_COUNT_PER_UNIT = 18;

/**
 * This wrapper is used to wrap the StudentsAlsoBought component with the RecommendationsProvider by passing the correct course.id value and contextId.
 */
export const StudentsAlsoBoughtRecommendationsWrapper = () => {
    const course = useClpCourse();

    const contextInput = useMemo(
        () => ({
            contextId: 'landing-page' as const,
            sourceObjects: [{id: course.id, type: RecommendationSourceObjectType.Course}],
            requestedItemCountPerUnit: RECOMMENDATIONS_ITEM_COUNT_PER_UNIT,
        }),
        [course.id],
    );

    return (
        <RecommendationsProvider
            contextInput={contextInput}
            includeAvailableSubscriptionInfo={true}
        >
            <BrowseEventTrackingProvider
                browseEventTrackingContext={{
                    relatedSourceId: course.id,
                    relatedSourceType: DiscoveryItemTypeOptions.COURSE,
                }}
            >
                <StudentsAlsoBoughtWrapper />
            </BrowseEventTrackingProvider>
        </RecommendationsProvider>
    );
};

/**
 * This wrapper is used to wrap the StudentsAlsoBought component with the logic needed to consume recos data from the RecommendationsProvider.
 */
export const StudentsAlsoBoughtWrapper = () => {
    const recommendationsSections = useRecommendationsSections();
    const recommendations = recommendationsSections.data[0];

    const courseRecommendations = useMemo(() => {
        if (!recommendations) return [];

        const courses: TrackableDiscoveryItem<BrowseCourse>[] = [];
        if ('items' in recommendations) {
            recommendations.items.forEach((item: RecommendationItem) => {
                if (item && typeof item === 'object' && 'course' in item && item.course) {
                    courses.push({
                        ...(item.course as BrowseCourse),
                        trackingId: item.trackingId,
                        frontendTrackingId: item.frontendTrackingId,
                    });
                }
            });
        }
        return courses;
    }, [recommendations]);

    // Don't render if no courses found
    if (!courseRecommendations.length) return null;

    return <StudentsAlsoBought courses={courseRecommendations} />;
};
